import { Injectable } from '@angular/core';
import { environment } from '@environment';

export interface BridgeResponse {
  id: string;
  ok: boolean;
  status: number;
  statusText: string;
  data?: unknown;
  error?: string;
}

interface Pending {
  resolve: (res: BridgeResponse) => void;
  reject: (err: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class BridgeService {
  private iframe: HTMLIFrameElement | null = null;
  private readyPromise: Promise<void> | null = null;
  private readonly pending = new Map<string, Pending>();
  private counter = 0;

  get enabled(): boolean {
    return !!environment.bridgeUrl && !isLocalHost();
  }

  prepare(): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    return this.ensureIframe();
  }

  private ensureIframe(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;

    this.readyPromise = new Promise<void>((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.src = environment.bridgeUrl;
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');

      const bridgeOrigin = new URL(environment.bridgeUrl).origin;

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== bridgeOrigin) return;
        const data = event.data;
        if (!data) return;

        if (data.type === 'bridge-ready') {
          resolve();
          return;
        }

        if (typeof data.id === 'string' && this.pending.has(data.id)) {
          const p = this.pending.get(data.id)!;
          this.pending.delete(data.id);
          p.resolve(data as BridgeResponse);
        }
      };

      window.addEventListener('message', onMessage);

      iframe.onerror = () => reject(new Error('Bridge iframe failed to load'));

      document.body.appendChild(iframe);
      this.iframe = iframe;
    });

    return this.readyPromise;
  }

  async request(
    method: string,
    url: string,
    headers: Record<string, string>,
    body: unknown,
  ): Promise<BridgeResponse> {
    await this.ensureIframe();

    const id = `req-${++this.counter}-${Date.now()}`;
    const bridgeOrigin = new URL(environment.bridgeUrl).origin;

    return new Promise<BridgeResponse>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.iframe!.contentWindow!.postMessage(
        { id, method, url, headers, body },
        bridgeOrigin,
      );
    });
  }
}

function isLocalHost(): boolean {
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}
